import { useDispatch, useSelector } from "react-redux";
import type { UnitCategory } from "@/types";
import {
    selectSelectedCategories,
    setSelectedCategories,
} from "@/features/filters/filtersSlice";

/** Placeholder widths (px) so the strip roughly matches real pills and reduces layout shift */
const SKELETON_PILL_WIDTHS = [52, 88, 72, 100, 80];

interface CategoryPillFiltersProps {
    categories: UnitCategory[];
    categoriesLoading?: boolean;
}

export const CategoryPillFilters = ({
    categories,
    categoriesLoading = false,
}: CategoryPillFiltersProps) => {
    const dispatch = useDispatch();
    const selectedCategories = useSelector(selectSelectedCategories);

    const isAllSelected = selectedCategories.length === 0;

    const handleSelectAll = () => {
        dispatch(setSelectedCategories([]));
    };

    const handleSelectCategory = (categoryId: number) => {
        const isSelected = selectedCategories.includes(categoryId);
        if (isSelected) {
            const newCategories = selectedCategories.filter(
                (id) => id !== categoryId,
            );
            dispatch(setSelectedCategories(newCategories));
        } else {
            dispatch(setSelectedCategories([...selectedCategories, categoryId]));
        }
    };

    if (categoriesLoading && categories.length === 0) {
        return (
            <div
                className="category-pill-filters category-pill-filters--skeleton"
                aria-busy="true"
                aria-label="Loading category filters"
            >
                {SKELETON_PILL_WIDTHS.map((widthPx, index) => (
                    <div
                        key={index}
                        className="category-pill category-pill-skeleton"
                        style={{ width: `${widthPx}px` }}
                    />
                ))}
            </div>
        );
    }

    if (categories.length === 0) {
        return null;
    }

    return (
        <div className="category-pill-filters">
            <button
                type="button"
                className={`category-pill ${isAllSelected ? "category-pill--active" : "category-pill--inactive"}`}
                onClick={handleSelectAll}
            >
                {isAllSelected && (
                    <svg
                        className="category-pill__check-icon"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                    >
                        <polyline points="20 6 9 17 4 12" />
                    </svg>
                )}
                All
            </button>
            {categories.map((category) => {
                const isSelected = selectedCategories.includes(category.id);
                return (
                    <button
                        key={category.id}
                        type="button"
                        className={`category-pill ${isSelected ? "category-pill--active" : "category-pill--inactive"}`}
                        onClick={() => handleSelectCategory(category.id)}
                    >
                        {isSelected && (
                            <svg
                                className="category-pill__check-icon"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2.5"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                            >
                                <polyline points="20 6 9 17 4 12" />
                            </svg>
                        )}
                        {category.name}
                    </button>
                );
            })}
        </div>
    );
};
